import type AppointmentComposeAdapter from './AppointmentComposeAdapter';
import type AppointmentReadAdapter from './AppointmentReadAdapter';
import type LoadedAdapter from './LoadedAdapter';
import type CommonAdapter from './CommonAdapter';
import type MessageComposeAdapter from './MessageComposeAdapter';
import type MessageReadAdapter from './MessageReadAdapter';
import type MultiSelectAdapter from './MultiSelectAdapter';
import { logGreyError } from 'owa-analytics/lib/api/logGreyError';
import { isCapabilityEnabled } from 'owa-capabilities';
import type { Capability } from 'owa-capabilities';
import type { MailboxInfo } from 'owa-client-types';

export type Adapter =
    | CommonAdapter
    | MessageComposeAdapter
    | MessageReadAdapter
    | AppointmentComposeAdapter
    | AppointmentReadAdapter
    | MultiSelectAdapter;

export type LoadedItemAdapter = LoadedAdapter & (MessageComposeAdapter | MessageReadAdapter);

interface Adapters {
    [hostItemIndex: string]: Adapter;
}

interface LoadedItemAdapters {
    [hostItemIndex: string]: LoadedItemAdapter;
}

const loadedItemAdapters: LoadedItemAdapters = {};

const adapters: Adapters = {};

export function addAdapter(hostItemIndex: string, adapter: Adapter): void {
    if (isAdapterPresent(hostItemIndex)) {
        deleteAdapter(hostItemIndex);
    }
    adapters[hostItemIndex] = adapter;
}

export function deleteAdapter(hostItemIndex: string): void {
    delete adapters[hostItemIndex];
}

export function getAdapter(hostItemIndex: string): Adapter {
    if (!isAdapterPresent(hostItemIndex)) {
        const error = new Error('Addin adapter not found');

        /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
         * BASELINE: DO NOT COPY/PASTE!
         *	> Argument at index 1 for logGreyError must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
        logGreyError(
            'AddinAdapterNotFound',
            undefined,
            error,
            {
                hostItemIndex,
                stack: error.stack,
            },
            {
                logVerbose: true,
                ring: 'Dogfood',
            }
        );
    }
    return adapters[hostItemIndex];
}

export function isAdapterPresent(hostItemIndex: string): boolean {
    return !!adapters[hostItemIndex];
}

/**
 * Determines whether an adapter should be created for the specified host item index.
 *
 * @param populateAddinsCapability - The capability object that determines if add-ins should be populated.
 * @param hostItemIndex - The ID containing surface type (e.g., Read/Compose/Multiselect) and the item ID.
 * @param mailboxInfo - Information about the mailbox to evaluate the capability for.
 * @returns True if the adapter should be created, otherwise false.
 */
export function shouldCreateAdapter(
    populateAddinsCapability: Capability,
    hostItemIndex: string,
    mailboxInfo: MailboxInfo
): boolean {
    return (
        isCapabilityEnabled(populateAddinsCapability, mailboxInfo) &&
        !isAdapterPresent(hostItemIndex)
    );
}

/**
 * Adds a loaded item adapter associated with the specified host item index and adds a reference to the control ID.
 *
 * @param hostItemIndex - The ID containing surface type (ex: Read/Compose/Multiselect) and the itemID.
 * @param adapter - The adapter that we are adding to the map.
 * @param controlId - The ID of the control to find the corresponding loaded item adapter for.
 */
export function addLoadedItemAdapter(
    hostItemIndex: string,
    adapter: LoadedItemAdapter,
    controlId: string
): void {
    if (!isLoadedItemAdapterPresent(hostItemIndex)) {
        loadedItemAdapters[hostItemIndex] = adapter;
        loadedItemAdapters[hostItemIndex].controlIds.push(controlId);
    }
}

/**
 * Adds a reference to the control ID in the loaded item adapter associated with the specified host item index.
 *
 * @param hostItemIndex - The ID containing surface type (ex: Read/Compose/Multiselect) and the itemID.
 * @param controlId - The ID of the control to find the corresponding loaded item adapter for.
 */
export function addReferenceToLoadedItemAdapter(hostItemIndex: string, controlId: string): void {
    if (
        isLoadedItemAdapterPresent(hostItemIndex) &&
        !loadedItemAdapters[hostItemIndex].controlIds.includes(controlId)
    ) {
        loadedItemAdapters[hostItemIndex].controlIds.push(controlId);
    }
}

/**
 * Determines if a loaded item adapter is present with the specified host item index.
 *
 * @param hostItemIndex - The ID containing surface type (ex: Read/Compose/Multiselect) and the itemID.
 * @returns True or False depending on whether the adapter was found.
 */
export function isLoadedItemAdapterPresent(hostItemIndex: string): boolean {
    return !!loadedItemAdapters[hostItemIndex];
}

/**
 * Determines if a loaded item adapter is present with the specified control ID.
 *
 * @param controlId - The ID of the control to find the corresponding loaded item adapter for.
 * @returns True or False depending on whether the adapter was found.
 */
export function isLoadedItemAdapterPresentWithControlId(controlId: string): boolean {
    const hostItemIndices = Object.keys(loadedItemAdapters);
    for (const hostItemIndex of hostItemIndices) {
        const loadedItemAdapter = loadedItemAdapters[hostItemIndex];
        if (loadedItemAdapter.controlIds.includes(controlId)) {
            return true;
        }
    }
    return false;
}

/**
 * Dereferences the control ID from the loaded item adapter associated with the specified control ID.
 * If the loaded item adapter no longer has any control IDs associated with it, it is removed.
 *
 * @param controlId - The ID of the control to find the corresponding loaded item adapter for.
 */
export function derefLoadedItemAdapter(controlId: string): void {
    const hostItemIndices = Object.keys(loadedItemAdapters);
    for (const hostItemIndex of hostItemIndices) {
        const loadedItemAdapter = loadedItemAdapters[hostItemIndex];
        if (loadedItemAdapter.controlIds.includes(controlId)) {
            loadedItemAdapter.controlIds = loadedItemAdapter.controlIds.filter(
                id => id !== controlId
            );
            if (loadedItemAdapter.controlIds.length === 0) {
                delete loadedItemAdapters[hostItemIndex];
            }
            return;
        }
    }
}

/**
 * Retrieves the loaded item adapter associated with the specified control ID.
 *
 * @param controlId - The ID of the control to find the corresponding loaded item adapter for.
 * @returns The loaded item adapter.
 */
export function getLoadedItemAdapterByControlId(controlId: string): LoadedItemAdapter | undefined {
    let resultHostItemIndex: string = '';
    const hostItemIndices = Object.keys(loadedItemAdapters);
    for (const hostItemIndex of hostItemIndices) {
        const loadedItemAdapter = loadedItemAdapters[hostItemIndex];
        if (loadedItemAdapter.controlIds.includes(controlId)) {
            resultHostItemIndex = hostItemIndex;
            break;
        }
    }
    return loadedItemAdapters[resultHostItemIndex];
}
